// Integrations — the connected-services status board.
//
// Read-mostly overview of what's wired up for this company: credit-monitoring
// affiliate links, the Credit Karma capture extension, email/SMS delivery,
// payment processors, and the inbound lead webhook. Editing lives in Company
// Settings and Billing; this page shows status and routes there.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, ExternalLink, Landmark, CreditCard, Mail, MessageSquare, Webhook, Camera } from "lucide-react";
import { Shell } from "../components/Shell";
import { company as companyApi, request } from "../lib/api";

type Status = "connected" | "action" | "off";

function StatusRow({ icon: Icon, title, detail, status, action }: {
  icon: any; title: string; detail: string; status: Status; action?: { label: string; onClick: () => void };
}) {
  const pill =
    status === "connected" ? { cls: "pill-green", txt: "Connected", ic: <CheckCircle2 size={13} aria-hidden="true" /> }
    : status === "action" ? { cls: "pill-red", txt: "Needs setup", ic: <AlertCircle size={13} aria-hidden="true" /> }
    : { cls: "", txt: "Optional", ic: null };
  return (
    <div className="table-row table-row--wide" style={{ alignItems: "flex-start" }}>
      <Icon size={20} aria-hidden="true" style={{ color: "var(--ne-chrome-2)", flex: "0 0 auto", marginTop: 2 }} />
      <div style={{ flex: "1 1 300px", minWidth: 0 }}>
        <strong>{title}</strong>
        <p className="muted small" style={{ margin: "3px 0 0" }}>{detail}</p>
        {action && (
          <button className="ghost-button" style={{ marginTop: 8 }} onClick={action.onClick}>
            {action.label}
          </button>
        )}
      </div>
      <span className={`pill ${pill.cls}`} style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
        {pill.ic}{pill.txt}
      </span>
    </div>
  );
}

// Same-origin on purpose: an absolute upstream URL sent a white-label
// tenant's staff to another company's site.
const CK_EXTENSION_HELP = "/blog";

export default function IntegrationsPage() {
  const nav = useNavigate();
  const [s, setS] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ghlToken, setGhlToken] = useState("");
  // SmartCredit / ConsumerDirect Partner API
  const [scId, setScId] = useState("");
  const [scSecret, setScSecret] = useState("");
  const [scEnv, setScEnv] = useState<"production" | "stage">("production");
  const [scBusy, setScBusy] = useState(false);
  const [scMsg, setScMsg] = useState<string | null>(null);
  async function saveSmartCredit() {
    setScBusy(true); setScMsg(null);
    try {
      await companyApi.update({ ...(s || {}),
        smartcredit_api_env: scEnv,
        ...(scId.trim() ? { smartcredit_client_id: scId.trim() } : {}),
        ...(scSecret.trim() ? { smartcredit_client_secret: scSecret.trim() } : {}),
      } as any);
      const r = await companyApi.get(); setS(r.settings || {});
      setScId(""); setScSecret("");
      setScMsg("Saved. Click Test connection to verify it works.");
    } catch (e: any) { setScMsg(e?.message || "Could not save."); }
    finally { setScBusy(false); }
  }
  async function testSmartCredit() {
    setScBusy(true); setScMsg(null);
    try {
      const r = await request<{ ok: true; environment: string; message: string }>("/api/integrations/smartcredit-test", { method: "POST", body: {} });
      setScMsg(`✓ ${r.message}`);
    } catch (e: any) { setScMsg(`✗ ${e?.message || "Test failed."}`); }
    finally { setScBusy(false); }
  }
  const [ghlLoc, setGhlLoc] = useState("");
  const [ghlMsg, setGhlMsg] = useState<string | null>(null);
  const [ghlBusy, setGhlBusy] = useState(false);

  async function saveGhl() {
    setGhlBusy(true); setGhlMsg(null);
    try {
      // company PUT nulls unlisted fields by design — send the full current
      // settings back with the GHL fields layered on.
      await companyApi.update({ ...(s || {}), ghl_location_id: ghlLoc.trim() || s?.ghl_location_id, ...(ghlToken.trim() ? { ghl_api_key: ghlToken.trim() } : {}) } as any);
      const r = await companyApi.get(); setS(r.settings || {});
      setGhlToken("");
      setGhlMsg("Saved. Now hit Test connection.");
    } catch (e: any) { setGhlMsg(e?.message || "Save failed"); }
    finally { setGhlBusy(false); }
  }

  async function testGhl() {
    setGhlBusy(true); setGhlMsg(null);
    try {
      const r = await request<any>("/api/integrations/ghl-test", { method: "POST", body: {} });
      setGhlMsg(`✅ Connected to "${r.location.name}" — leads and contacts can flow.`);
    } catch (e: any) { setGhlMsg(`❌ ${e?.message || "Test failed"}`); }
    finally { setGhlBusy(false); }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await companyApi.get();
        setS(r.settings || {});
        if ((r as any)?.settings?.smartcredit_api_env === "stage") setScEnv("stage");
      } catch (e: any) {
        setErr(e?.message || "Could not load integrations.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const emailOn = !!(s?.from_email);
  const smsOn = !!(s?.twilio_configured || (s?.comms_provider === "twilio"));
  const mfsn = !!s?.mfsn_affiliate_url;
  const sc = !!s?.smartcredit_affiliate_url;

  return (
    <Shell title="Integrations" subtitle="Connected services & partner links">
      {loading && <p className="muted small">Loading…</p>}
      {err && <p className="error-text">{err}</p>}

      {!loading && !err && (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><div><h2>Credit monitoring & affiliates</h2>
              <span className="muted small">Your enroll links earn you the commission when a client signs up. Set them in Company Settings.</span></div></div>
            <div className="table-list">
              <StatusRow
                icon={Landmark}
                title="MyFreeScoreNow affiliate link"
                detail={mfsn ? "Your enroll link is set — shown to clients on the intake step who don't have monitoring yet." : "Not set. Add your enroll link so clients without monitoring sign up through you and you earn the commission."}
                status={mfsn ? "connected" : "action"}
                action={{ label: mfsn ? "Edit in Settings" : "Add your link", onClick: () => nav("/settings") }}
              />
              <StatusRow
                icon={Landmark}
                title="SmartCredit affiliate link"
                detail={sc ? "Your enroll link is set." : "Optional — add a SmartCredit enroll link if you use them too."}
                status={sc ? "connected" : "off"}
                action={{ label: sc ? "Edit in Settings" : "Add link", onClick: () => nav("/settings") }}
              />
              <StatusRow
                icon={Landmark}
                title="iSoftpull soft-pull prequalification"
                detail={s?.softpull_configured ? "Connected — run no-score-impact bureau pulls from any client's Applications tab." : "Connect your iSoftpull API keys in Settings to prequalify clients instantly (soft pull, no score impact, no client login needed)."}
                status={s?.softpull_configured ? "connected" : "off"}
                action={{ label: s?.softpull_configured ? "Manage in Settings" : "Connect in Settings", onClick: () => nav("/settings") }}
              />
              <StatusRow
                icon={Camera}
                title="NX Credit Karma Capture extension"
                detail="Credit Karma can't be pulled server-side (bot-blocked), so you capture a client's CK report + pre-quals from your own logged-in browser with the Chrome extension, which posts it straight into the client's file."
                status="connected"
                action={{ label: "How it works", onClick: () => window.open(CK_EXTENSION_HELP, "_blank") }}
              />
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><div><h2>SmartCredit API (ConsumerDirect)</h2>
              <span className="muted small">Your ConsumerDirect Partner API key. When connected, SmartCredit reports import through the official API — no client login, and it bypasses the bot wall that blocks the browser scrape.</span></div>
              <span className={`pill ${s?.smartcredit_api_configured ? "pill-green" : ""}`}>{s?.smartcredit_api_configured ? `Connected (${s?.smartcredit_api_env || "production"})` : "Not connected"}</span>
            </div>
            <div className="form-row-2">
              <label className="form-field">
                <span>Client ID</span>
                <input type="password" value={scId} onChange={(e) => setScId(e.target.value)}
                  placeholder={s?.smartcredit_api_configured ? "•••••• saved — paste to replace" : "client key from ConsumerDirect"} autoComplete="off" />
              </label>
              <label className="form-field">
                <span>Client Secret</span>
                <input type="password" value={scSecret} onChange={(e) => setScSecret(e.target.value)}
                  placeholder={s?.smartcredit_api_configured ? "•••••• saved — paste to replace" : "client secret from ConsumerDirect"} autoComplete="off" />
              </label>
            </div>
            <label className="form-field" style={{ maxWidth: 260, marginTop: 8 }}>
              <span>Environment</span>
              <select value={scEnv} onChange={(e) => setScEnv(e.target.value as "production" | "stage")}>
                <option value="production">Production (live keys)</option>
                <option value="stage">Stage / sandbox (test keys)</option>
              </select>
            </label>
            <p className="muted small" style={{ margin: "8px 0 10px" }}>
              Keys are issued per environment by ConsumerDirect (partnerintegration@consumerdirect.com). Stored encrypted, used only for your company. If Test says the IP isn't whitelisted, ask them to whitelist Cloudflare egress for your partner account.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="primary-button" disabled={scBusy || (!scId.trim() && !scSecret.trim() && scEnv === (s?.smartcredit_api_env || "production"))} onClick={saveSmartCredit}>
                {scBusy ? "Working…" : "Save"}
              </button>
              <button className="ghost-button" disabled={scBusy || !s?.smartcredit_api_configured} onClick={testSmartCredit}>
                Test connection
              </button>
            </div>
            {scMsg && <p className="muted small" style={{ marginTop: 10 }}>{scMsg}</p>}
          </div>

          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><div><h2>GoHighLevel / LeadConnector</h2>
              <span className="muted small">Connect your GHL sub-account so leads and contacts can flow between systems.</span></div>
              <span className={`pill ${s?.ghl_configured ? "pill-green" : ""}`}>{s?.ghl_configured ? "Token saved" : "Not connected"}</span>
            </div>
            <div className="form-row-2">
              <label className="form-field">
                <span>Private Integration token</span>
                <input type="password" value={ghlToken} onChange={(e) => setGhlToken(e.target.value)}
                  placeholder={s?.ghl_configured ? "•••••• saved — paste to replace" : "pit-… from GHL → Settings → Private Integrations"} autoComplete="off" />
              </label>
              <label className="form-field">
                <span>Location ID (sub-account)</span>
                <input value={ghlLoc} onChange={(e) => setGhlLoc(e.target.value)}
                  placeholder={s?.ghl_location_id || "Found in GHL → Settings → Business Profile"} />
              </label>
            </div>
            <p className="muted small" style={{ margin: "6px 0 10px" }}>
              In GHL: Settings → Private Integrations → create one with Contacts + Locations scopes, copy the token.
              We store it encrypted and only use it for your company's account.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="primary-button" disabled={ghlBusy || (!ghlToken.trim() && !ghlLoc.trim())} onClick={saveGhl}>
                {ghlBusy ? "Saving…" : "Save"}
              </button>
              <button className="ghost-button" disabled={ghlBusy || !s?.ghl_configured} onClick={testGhl}>
                Test connection
              </button>
            </div>
            {ghlMsg && <p className="muted small" style={{ marginTop: 10 }}>{ghlMsg}</p>}
          </div>

          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><div><h2>Communications</h2></div></div>
            <div className="table-list">
              <StatusRow
                icon={Mail}
                title="Email delivery"
                detail={emailOn ? `Sending from ${s.from_email}. Intake links, contracts, and portal emails all go out through this.` : "Using the platform default sender. Set your own from-address in Company Settings for branded email."}
                status={emailOn ? "connected" : "off"}
                action={{ label: "Email settings", onClick: () => nav("/settings") }}
              />
              <StatusRow
                icon={MessageSquare}
                title="SMS (Twilio)"
                detail={smsOn ? "Twilio is configured — intake links can be texted." : "Optional. Connect Twilio in Company Settings to text intake links instead of emailing them."}
                status={smsOn ? "connected" : "off"}
                action={{ label: "Connect Twilio", onClick: () => nav("/settings") }}
              />
            </div>
          </div>

          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header"><div><h2>Payments</h2></div></div>
            <div className="table-list">
              <StatusRow
                icon={CreditCard}
                title="Payment processors — Stripe · Authorize.net · Fanbasis · Whop"
                detail="Connect your own merchant account and charge clients with hosted checkout links. Note: Stripe prohibits credit-repair billing — use it for funding/consulting services only."
                status="off"
                action={{ label: "Connect on Billing", onClick: () => nav("/billing") }}
              />
            </div>
          </div>

          <div className="panel">
            <div className="panel-header"><div><h2>Inbound leads</h2>
              <span className="muted small">Send leads from another form or funnel straight into your client list.</span></div></div>
            <div className="table-list">
              <StatusRow
                icon={Webhook}
                title="Inbound client webhook"
                detail="POST a lead to /api/webhooks/inbound-client (signed with your HMAC secret) and it creates a client automatically. Ask us to enable your signing secret if you want to wire up an external funnel."
                status="off"
              />
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}
